import type { ReactNode } from "react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { AuthGuard } from "@/components/auth-guard";
import { SiteManagerGuard } from "@/components/role-guard";
import { canAccessOnboardingInDevelopment } from "@/lib/onboarding";
import { useOptionalUser } from "@/lib/user-context";
import App from "./App";
import ErrorPage from "./ErrorPage";
import AdminAuditLog from "./routes/admin.audit-log";
import AdminBillingOversight from "./routes/admin.billing-oversight";
import AdminSites from "./routes/admin.sites";
import AdminSystemHealth from "./routes/admin.system-health";
import UnresolvedEmail from "./routes/admin.unresolved-email";
import AdminUsers from "./routes/admin.users";
import BillingOverview from "./routes/billing";
import Dashboard from "./routes/dashboard";
import DevDesignSystem from "./routes/dev-design-system";
import EmailInbox from "./routes/email._index";
import EmailDetail from "./routes/email.$id";
import ForgotPassword from "./routes/forgot-password";
import ManagerFormsIndex from "./routes/forms.manager._index";
import ManagerFormDetail from "./routes/forms.manager.$formId";
import ManagerTemplatesIndex from "./routes/forms.manager.templates._index";
import ManagerTemplateDetail from "./routes/forms.manager.templates.$templateId";
import Login from "./routes/login";
import NotesIndex from "./routes/notes._index";
import NoteDetail from "./routes/notes.$id";
import Onboarding from "./routes/onboarding";
import Profile from "./routes/profile._index";
import ProfileAccount from "./routes/profile.account";
import ProfileEdit from "./routes/profile.edit";
import ProfileSignInActivity from "./routes/profile.sign-in-activity";
import Qr from "./routes/qr";
import RecordsIndex from "./routes/records._index";
import Reports from "./routes/reports";
import ResetPassword from "./routes/reset-password";
import SignIn from "./routes/sign-in";
import SignUp from "./routes/sign-up";
import SignInsIndex from "./routes/signins._index";
import SignInDetail from "./routes/signins.$signInId";
import SiteIndex from "./routes/site._index";
import SiteLaunchPad from "./routes/site.$id";
import SiteEdit from "./routes/site.edit";
import SiteEntranceEdit from "./routes/site.entrance.edit";
import SiteEntranceNew from "./routes/site.entrance.new";
import SiteEntrances from "./routes/site.entrances";
import SiteNew from "./routes/site.new";
import SiteSignIns from "./routes/site.signins";
import TrainingIndex from "./routes/training._index";
import WorkersIndex from "./routes/workers._index";
import WorkerProfile from "./routes/workers.$id";
import WorkerApprovals from "./routes/workers.$id.approvals";
import WorkerCertifications from "./routes/workers.$id.certifications";
import WorkerSignIns from "./routes/workers.$id.sign-ins";

function OnboardingRouteGuard({ children }: { children: ReactNode }) {
  const location = useLocation();
  const user = useOptionalUser();
  const allowDevelopmentOnboardingAccess = canAccessOnboardingInDevelopment({
    pathname: location.pathname,
    isDevelopment: import.meta.env.DEV,
    systemRole: user?.systemRole ?? null,
  });

  if (allowDevelopmentOnboardingAccess) {
    return <>{children}</>;
  }

  if (user?.systemRole && user.systemRole !== "site_manager") {
    return <Navigate to="/dashboard" replace />;
  }

  return <SiteManagerGuard>{children}</SiteManagerGuard>;
}

const routes = [
  {
    path: "/",
    element: <App />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <Navigate to="/sign-in" replace />,
        handle: { isFullScreen: true },
      },
      { path: "login", element: <Login />, handle: { isFullScreen: true } },
      { path: "sign-in", element: <SignIn />, handle: { isFullScreen: true } },
      { path: "sign-up", element: <SignUp />, handle: { isFullScreen: true } },
      {
        path: "forgot-password",
        element: <ForgotPassword />,
        handle: { isFullScreen: true },
      },
      {
        path: "reset-password",
        element: <ResetPassword />,
        handle: { isFullScreen: true },
      },
      {
        element: (
          <AuthGuard>
            <Outlet />
          </AuthGuard>
        ),
        children: [
          {
            path: "dashboard",
            element: <Dashboard />,
          },
          ...(import.meta.env.DEV
            ? [
                {
                  path: "dev/design-system",
                  element: <DevDesignSystem />,
                },
              ]
            : []),
          {
            path: "onboarding",
            element: (
              <OnboardingRouteGuard>
                <Onboarding />
              </OnboardingRouteGuard>
            ),
            handle: { isFullScreen: true, allowScroll: true },
          },
          {
            path: "reports",
            element: (
              <SiteManagerGuard>
                <Reports />
              </SiteManagerGuard>
            ),
          },
          {
            path: "inbox",
            element: (
              <SiteManagerGuard>
                <Navigate to="/reports" replace />
              </SiteManagerGuard>
            ),
          },
          {
            path: "billing",
            element: (
              <SiteManagerGuard>
                <BillingOverview />
              </SiteManagerGuard>
            ),
          },
          {
            path: "forms",
            element: <Navigate to="/forms/permits" replace />,
          },
          {
            path: "forms/permits",
            element: (
              <SiteManagerGuard>
                <ManagerFormsIndex />
              </SiteManagerGuard>
            ),
          },
          {
            path: "forms/permits/:formId",
            element: (
              <SiteManagerGuard>
                <ManagerFormDetail />
              </SiteManagerGuard>
            ),
          },
          {
            path: "forms/templates",
            element: (
              <SiteManagerGuard>
                <ManagerTemplatesIndex />
              </SiteManagerGuard>
            ),
          },
          {
            path: "forms/templates/:templateId",
            element: (
              <SiteManagerGuard>
                <ManagerTemplateDetail />
              </SiteManagerGuard>
            ),
          },
          {
            path: "forms/training",
            element: (
              <SiteManagerGuard>
                <TrainingIndex />
              </SiteManagerGuard>
            ),
          },
          {
            path: "forms/training/:formId",
            element: (
              <SiteManagerGuard>
                <ManagerFormDetail />
              </SiteManagerGuard>
            ),
          },
          {
            path: "forms/records",
            element: (
              <SiteManagerGuard>
                <RecordsIndex />
              </SiteManagerGuard>
            ),
          },
          {
            path: "forms/records/:formId",
            element: (
              <SiteManagerGuard>
                <ManagerFormDetail />
              </SiteManagerGuard>
            ),
          },
          {
            path: "account",
            element: <Profile />,
            errorElement: <ErrorPage />,
            children: [
              { index: true, element: <ProfileAccount /> },
              { path: "edit-profile", element: <ProfileEdit /> },
              { path: "sign-in-activity", element: <ProfileSignInActivity /> },
            ],
          },
          {
            path: "sites",
            element: (
              <SiteManagerGuard>
                <SiteIndex />
              </SiteManagerGuard>
            ),
            errorElement: <ErrorPage />,
            children: [
              { path: "new", element: <SiteNew /> },
              { path: ":id", element: <SiteLaunchPad /> },
              { path: ":id/edit", element: <SiteEdit /> },
              { path: ":id/entrances", element: <SiteEntrances /> },
              { path: ":id/entrances/new", element: <SiteEntranceNew /> },
              {
                path: ":id/entrances/:entranceId/edit",
                element: <SiteEntranceEdit />,
              },
              { path: ":id/signins", element: <SiteSignIns /> },
              {
                path: ":id/qr",
                element: <Qr />,
                handle: { isFullScreen: true },
              },
            ],
          },
          {
            path: "workers",
            element: (
              <SiteManagerGuard>
                <WorkersIndex />
              </SiteManagerGuard>
            ),
            errorElement: <ErrorPage />,
            children: [
              { path: ":id", element: <WorkerProfile /> },
              { path: ":id/sign-ins", element: <WorkerSignIns /> },
              { path: ":id/approvals", element: <WorkerApprovals /> },
              { path: ":id/certifications", element: <WorkerCertifications /> },
            ],
          },
          {
            path: "signins",
            element: (
              <SiteManagerGuard>
                <SignInsIndex />
              </SiteManagerGuard>
            ),
            errorElement: <ErrorPage />,
            children: [
              {
                path: ":signInId",
                element: <SignInDetail />,
              },
            ],
          },
          {
            path: "email",
            element: (
              <SiteManagerGuard>
                <EmailInbox />
              </SiteManagerGuard>
            ),
          },
          {
            path: "email/:id",
            element: (
              <SiteManagerGuard>
                <EmailDetail />
              </SiteManagerGuard>
            ),
          },
          {
            path: "notes",
            element: (
              <SiteManagerGuard>
                <NotesIndex />
              </SiteManagerGuard>
            ),
            errorElement: <ErrorPage />,
            children: [
              {
                path: ":id",
                element: <NoteDetail />,
              },
            ],
          },
          {
            path: "admin",
            element: <Navigate to="/dashboard" replace />,
          },
          {
            path: "admin/sites",
            element: <AdminSites />,
          },
          {
            path: "admin/users",
            element: <AdminUsers />,
          },
          {
            path: "admin/system-health",
            element: <AdminSystemHealth />,
          },
          {
            path: "admin/billing-oversight",
            element: <AdminBillingOversight />,
          },
          {
            path: "admin/audit-log",
            element: <AdminAuditLog />,
          },
          {
            path: "admin/unresolved-email",
            element: <UnresolvedEmail />,
          },
        ],
      },
    ],
  },
];

const router = createBrowserRouter(routes);

export default router;
